/**
 * ProposalCard - Renders an agent proposal inline within a message bubble.
 *
 * Appears below the agent message text when a [[PROPOSAL:#A12:label]] token is
 * found in the message content. Shows a compact card with entity type icon,
 * ref badge, action label, summary, and status-based action buttons.
 *
 * Props:
 *   suggestion   - Full Firestore suggestion doc data (may be null if not yet loaded)
 *   onApprove    - (suggestionId) => void
 *   onDismiss    - (suggestionId) => void
 *   onModify     - (suggestionId, modifiedNote) => void
 *   canApprove   - boolean; hide action buttons when false (e.g. shared-trip viewer)
 */

import React, { useState } from 'react';
import './ProposalCard.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getEntityIcon(suggestion) {
  if (!suggestion) return '💡';
  const service = (suggestion.action?.service || '').toLowerCase();
  const entityType = (suggestion.entityType || '').toLowerCase();
  const combined = service || entityType;
  if (combined.includes('accommodation') || combined.includes('hotel')) return '🏨';
  if (combined.includes('travel') || combined.includes('flight')) return '✈️';
  if (combined.includes('place')) return '📍';
  if (combined.includes('day')) return '📅';
  return '💡';
}

function getStatusLabel(status) {
  switch (status) {
    case 'approved': return 'Approved';
    case 'dismissed': return 'Dismissed';
    case 'modified': return 'Modified';
    default: return 'Pending';
  }
}

// ---------------------------------------------------------------------------
// ProposalCard Component
// ---------------------------------------------------------------------------

const ProposalCard = ({
  suggestion,
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const [modifyMode, setModifyMode] = useState(false);
  const [modifyNote, setModifyNote] = useState('');

  // Skeleton state: suggestion not yet loaded from Firestore
  if (!suggestion) {
    return (
      <div className="proposal-card proposal-card--skeleton">
        <div className="proposal-card__skeleton-line proposal-card__skeleton-line--short" />
        <div className="proposal-card__skeleton-line" />
        <div className="proposal-card__skeleton-line proposal-card__skeleton-line--medium" />
      </div>
    );
  }

  const { id, refId, status = 'outstanding', action = {} } = suggestion;
  const isOutstanding = status === 'outstanding';
  const icon = getEntityIcon(suggestion);
  const statusLabel = getStatusLabel(status);

  const handleApprove = () => {
    if (typeof onApprove === 'function') onApprove(id);
  };

  const handleDismiss = () => {
    if (typeof onDismiss === 'function') onDismiss(id);
  };

  const handleModifyConfirm = () => {
    if (typeof onModify === 'function') onModify(id, modifyNote.trim());
    setModifyMode(false);
    setModifyNote('');
  };

  const handleModifyCancel = () => {
    setModifyMode(false);
    setModifyNote('');
  };

  return (
    <div
      className={[
        'proposal-card',
        `proposal-card--${status}`
      ].join(' ')}
      role="article"
      aria-label={`Suggestion #${refId}: ${action.label || 'Suggestion'}`}
    >
      {/* Card header row */}
      <div className="proposal-card__header">
        <span className="proposal-card__icon" aria-hidden="true">{icon}</span>
        <span className="proposal-card__ref">#{refId}</span>
        <span className="proposal-card__title">{action.label || 'Suggestion'}</span>
        {!isOutstanding && (
          <span className={`proposal-card__status-badge proposal-card__status-badge--${status}`}>
            {statusLabel}
          </span>
        )}
      </div>

      {/* Summary / description */}
      {action.summary && (
        <p className="proposal-card__summary">{action.summary}</p>
      )}

      {/* Action buttons — only when outstanding and canApprove */}
      {isOutstanding && canApprove && !modifyMode && (
        <div className="proposal-card__actions">
          <button
            className="proposal-card__btn proposal-card__btn--approve"
            onClick={handleApprove}
            type="button"
          >
            Approve
          </button>
          <button
            className="proposal-card__btn proposal-card__btn--modify"
            onClick={() => setModifyMode(true)}
            type="button"
          >
            Modify
          </button>
          <button
            className="proposal-card__btn proposal-card__btn--dismiss"
            onClick={handleDismiss}
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Modify inline form */}
      {isOutstanding && canApprove && modifyMode && (
        <div className="proposal-card__modify-form">
          <textarea
            className="proposal-card__modify-input"
            placeholder="Describe the change you'd like (e.g. 'change dates to March 20–25')..."
            value={modifyNote}
            onChange={(e) => setModifyNote(e.target.value)}
            rows={3}
            autoFocus
          />
          <div className="proposal-card__modify-actions">
            <button
              className="proposal-card__btn proposal-card__btn--approve"
              onClick={handleModifyConfirm}
              type="button"
              disabled={!modifyNote.trim()}
            >
              Confirm
            </button>
            <button
              className="proposal-card__btn proposal-card__btn--dismiss"
              onClick={handleModifyCancel}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Outstanding with canApprove=false: read-only pending notice */}
      {isOutstanding && !canApprove && (
        <p className="proposal-card__readonly-notice">
          Only the trip owner can approve or dismiss suggestions.
        </p>
      )}
    </div>
  );
};

export default ProposalCard;
